import React from "react";
import styled from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { FaSteam } from "react-icons/fa";

import { SteambotEnroll } from "MVVM/Components/SteambotEnroll";
import { useSteambot } from "MVVM/Hooks/useSteambot";
import { usePortal } from "MVVM/Hooks/usePortal";
import { NewPill } from "MVVM/Components/ClipQualitySettings/ClipQualityBubble";
import { analytics } from "MVVM/Analytics";

const DISMISS_KEY = "steambot-bubble-dismissed";
const PROMO = { type: "bubble", promotionName: "steambotBubble" };

export const SteambotBubble = () => {
  const { showBubble } = useSteambot();
  const [dismissed, setDismissed] = React.useState(() =>
    Boolean(localStorage.getItem(DISMISS_KEY)),
  );
  // portalled to #root so the modal is a normal centered modal instead of
  // being trapped inside the bottom-right bubble stack
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <ModalBody>
      <ModalTitle>Add the Allstar Steam Bot</ModalTitle>
      <SteambotEnroll />
    </ModalBody>,
  );

  const visible = showBubble && !dismissed;

  React.useEffect(() => {
    if (visible) analytics.track("Promotion - Impression", PROMO);
  }, [visible]);

  // stay mounted while the modal is open even after enrolling (showBubble goes
  // false the moment the bot accepts) so the user sees the success state
  if (!visible && !portalOpen) return null;

  const onDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
    analytics.track("Promotion - Dismiss", PROMO);
  };

  return (
    <>
      {visible && (
        <BubbleContainer
          onClick={() => {
            analytics.track("Promotion - Button Click", PROMO);
            setPortalOpen(true);
          }}
        >
          <BubbleIcon>
            <FaSteam size={20} />
          </BubbleIcon>
          <BubbleText>
            Add Allstar Steam Bot for instant AutoCapture clips
          </BubbleText>
          <NewPill>NEW</NewPill>
          <DismissButton onClick={onDismiss}>✕</DismissButton>
        </BubbleContainer>
      )}
      {portalOpen && Portal}
    </>
  );
};

const BubbleContainer = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  gap: 8px;
  padding: 8px 16px 8px 12px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const BubbleIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
`;

const BubbleText = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 520px) {
    white-space: normal;
  }
`;

const DismissButton = styled(Flex)`
  align-items: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.shade400};
  cursor: pointer;
  font-size: 12px;
  height: 20px;
  justify-content: center;
  transition: color 0.15s ease;
  width: 20px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const ModalBody = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  gap: 16px;
  max-height: 90vh;
  max-width: 480px;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  width: 90vw;
`;

const ModalTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 20px;
  font-weight: 700;
`;
